import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { ModalController } from '@ionic/angular';
import { finalize, forkJoin } from 'rxjs';
import {
  QuestionIncrementalRequest,
  QuestionRequest,
} from 'src/app/models/flow';
import { Question } from 'src/app/models/question';
import { AuthService } from 'src/app/services/auth.service';
import { ConfigService } from 'src/app/services/config.service';
import { ToolsService } from 'src/app/services/tools.service';
import { ButtonsModalComponent } from '../buttons-modal/buttons-modal.component';

import { ReCaptchaV3Service } from 'ng-recaptcha';

@Component({
  selector: 'app-progressive-profile',
  templateUrl: './progressive-profile.component.html',
  styleUrls: ['./progressive-profile.component.scss'],
})
export class ProgressiveProfileComponent implements OnInit, OnDestroy {
  @Input() flow!: any;
  @Input() clientId!: number;
  @Input() isStart!: boolean;
  @Output() onBack: EventEmitter<any> = new EventEmitter<any>();
  @Output() onContinue: EventEmitter<any> = new EventEmitter<any>();
  billQuestions: Array<Question> = [];
  loading: boolean = false;
  loadingQuestions: boolean = false;

  @Input() data: any;

  pqrsMode = false;

  captcha: string = '';

  constructor(
    private configService: ConfigService,
    private toolService: ToolsService,
    private authService: AuthService,
    private modalCtrl: ModalController,
    private router: Router,
    private recaptchaV3Service: ReCaptchaV3Service,
  ) {}

  ngOnDestroy(): void {
    this.flow = null;
  }
  ngOnInit() {
    this.getFlow();
    if (this.data?.pqrs) {
      this.pqrsMode = true;
    }

  }

  goBack() {
    this.onBack.emit();
  }

  validateCaptcha(action = 'login') {
    if (this.configService.mall?.captcha_token) {
      this.recaptchaV3Service.execute(action).subscribe((token: string) => {
        this.captcha = token;
      });
    }
  }

  getFlow() {

    this.loadingQuestions = true;
    // if (this.flow) {
    //   this.loadQuestions();
    //   return;
    // }
    this.configService.getFlowById().subscribe({
      next: (data) => {
        this.flow = data;
        this.loadQuestions();
      },
      error: (err) => {
        this.loadingQuestions = false;
        console.error('GetFlowById error', err);
        this.toolService.presentErrorToast(
          'Ocurrió un error inesperado, intenta de nuevo más tarde'
        );
      },
    });
  }

  continue() {
    // Primero, validar el formulario
    let hasError: boolean = false;

    for (let question of this.billQuestions) {
      if (question.formControl.invalid) {
        question.formControl.markAsTouched();
        hasError = true;
      }
    }

    if (hasError) return;

    this.loading = true;

    const { incremental, standard } = this.getValuesArray();
    const requests = [];

    if (incremental.length) {
      requests.push(this.configService.saveIncrementalResponse(incremental));
    }

    if (standard.length) {
      requests.push(this.configService.saveResponse(standard));
    }

    const handleNavigation = () => {

      const document: any = window.localStorage.getItem('document_number');
      const document_type: any = window.localStorage.getItem('document_type');

      if (!document || !document_type) {
        console.error('No user data in sessionStorage');
        return;
      }

      this.validateCaptcha()

      this.authService.login(document_type, document, this.captcha).subscribe({
        next: (lite_token: { refresh_token: string; access_token: string }) => {

          // save tokens
          this.authService.setToken(lite_token.refresh_token);
          this.authService.saveLiteToken(lite_token.access_token);

          this.authService.saveUserAuth(document, document_type);
          this.configService.document_number = document;
          this.configService.document_type = document_type;
          this.configService.saveUserLoginData(document_type, document);

          // exception for pqrs
          if (this.pqrsMode) {
            this.router.navigate(['client-pqr'], {
              queryParams: { pqrs: 1 }
            });
            return;
          }

          if (this.isStart) {
            this.onContinue.emit({ next: 'login' });

            if (!this.toolService.start) {
              window.sessionStorage.removeItem('user');
              window.sessionStorage.removeItem('lite_token');
            }

            this.router.navigate(['/']);
          } else {
            this.onContinue.emit({ next: 'upload-bill' });
          }

        },
        error: (err) => {
          console.error('Error re-login user', err);
          this.toolService.presentErrorToast(
            'No se pudo iniciar sesión nuevamente'
          );
        }
      });
    };

    if (requests.length > 0) {
      forkJoin(requests).subscribe({
        next: () => {
          this.loading = false;
          handleNavigation();
        },
        error: (err) => {
          this.loading = false;
          console.error('Error al guardar respuestas', err);
          this.flow = null;
          this.getFlow();
          this.toolService.presentErrorToast(
            'Ocurrió un error inesperado, intenta de nuevo más tarde'
          );
        },
      });
    } else {
      this.loading = false;
      handleNavigation();
    }
  }

  getValuesArray(): {
    incremental: QuestionRequest[];
    standard: QuestionRequest[];
  } {
    const groupedValues = {
      incremental: [] as QuestionIncrementalRequest[],
      standard: [] as QuestionRequest[],
    };

    for (const question of this.billQuestions) {
      const questionId = question.id ?? question.label;
      let response: any = this.toolService.getValueFromQuestion(
        question.label,
        this.billQuestions
      );

      const isEmptyArray = Array.isArray(response) && response.length === 0;

      const isTrulyEmpty = response === null || response === undefined || response === '';


      if (!isEmptyArray && isTrulyEmpty) {
        response = ' ';
      }

      if (!isEmptyArray && response !== undefined && response !== null) {
        const formattedAnswer = Array.isArray(response)
          ? response.join(',')
          : response;

        if (question.incremental_info && question.question_id) {
          groupedValues.incremental.push({
            question: question.question_id,
            answer: formattedAnswer,
          });
        } else {
          groupedValues.standard.push({
            question: questionId,
            response: formattedAnswer,
          });
        }
      }
    }

    return groupedValues;
  }


  loadQuestions() {
    this.billQuestions = this.flow.map((question: any) => {
      console.debug('ProgressiveProfileComponent.loadQuestions()', question);
      let formControl: FormControl = new FormControl('', Validators.required);
      let validators = [];
      let options = null;
      let maxDate: Date = new Date();
      question.type = question.type.toLowerCase();

      let errorTextValues: any = {
        required: 'Este campo es obligatorio',
        email:
          'Por favor, introduce una dirección de correo electrónico válida',
      };

      if (question.validation) {
        for (const validation in question.validation) {
          if (validation === 'maxDate') {
            maxDate = new Date(question.validation[validation]);
          } else if (validation === 'minLength') {
            validators.push(
              Validators.minLength(question.validation[validation])
            );
            errorTextValues[
              'minLength'
            ] = `La longitud mínima permitida es ${question.validation[validation]}`;
          } else if (validation === 'maxLength') {
            validators.push(
              Validators.maxLength(question.validation[validation])
            );
            errorTextValues[
              'maxLength'
            ] = `La longitud máxima permitida es ${question.validation[validation]}`;
          } else if (validation === 'pattern') {
            validators.push(
              Validators.pattern(question.validation[validation])
            );
            errorTextValues['pattern'] = `El formato no es válido`;
          }
        }
      }

      if (question.type === 'number') {
        question?.required && validators.push(Validators.required);
        formControl = new FormControl(null, validators);
      } else if (question.type === 'text') {
        question?.required && validators.push(Validators.required);
        formControl = new FormControl(null, validators);
      } else if (question.type === 'email') {
        question?.required && validators.push(Validators.required);
        validators.push(Validators.email);
        formControl = new FormControl(null, validators);
      } else if (question.type === 'phone') {
        question?.required && validators.push(Validators.required);
        formControl = new FormControl(null, validators);
      } else if (
        question.type === 'select' ||
        question.type === 'multiple-select'
      ) {
        let data: any[] = [];
        if (typeof question?.options === 'string') {
          data = question.options.split(',');
        } else if (Array.isArray(question.options)) {
          data = question.options;
        } else if (
          typeof question?.options === 'object' &&
          question.options !== null
        ) {
          data = Object.keys(question.options);
        }
        options = data.map((option: any) => {
          return { value: option, label: option };
        });
        if (question?.required) {
          validators.push(Validators.required);
        }
        formControl = new FormControl(null, validators);
      } else if (question.type === 'check') {
        formControl = new FormControl(true);
      } else if (question.type === 'date') {
        question?.required && validators.push(Validators.required);
        formControl = new FormControl(null, validators);
        if (question.validation?.maxDate) {
          maxDate = new Date(question.validation.maxDate);
        }
      }

      let questionLabel = question.label;
      if (question.required) questionLabel += ' *';

      let question_final = {
        id: question.name,
        label: questionLabel,
        type: (question.type === 'number'
          ? 'text'
          : question.type === 'multiselect'
          ? 'select'
          : question.type === 'check'
          ? 'checkbox'
          : question.type === 'phone'
          ? 'tel'
          : question.type) as any,
        formControl: formControl,
        multiple: question.type === 'multiselect' ? true : false,
        options: options as any,
        maxDate: maxDate.toISOString(),
        inputType: question.type === 'check' ? 'checkbox' : question.type,
        errorText: errorTextValues,
        suggestions: question.suggestions,
        question_id: question.question_id,
        incremental_info: question.incremental_info ?? false,
        url: question.url,
        depends_on: question.depends_on || null,
        minDate: question.validation?.minDate || null,

      };

      if(question?.validations?.min_years){
        const today = new Date();
        const minDate = new Date(today.getFullYear() - question.validations.min_years, today.getMonth(), today.getDate());
        question_final.minDate = minDate.toISOString();
      }

      return question_final;
    });

    this.loadingQuestions = false;
  }

  async getButtons() {
    this.loading = true;
    this.authService
      .getButtonsRoles()
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (response) => {
          if (response.length > 0) {
            this.openModalButtons(response);
          }
        },
        error: (error) => {
          console.error('Error obteniendo los botones:', error);
        },
      });
  }

  async openModalButtons(buttons: any) {
    const modal = await this.modalCtrl.create({
      component: ButtonsModalComponent,
      cssClass: 'custom-modal',
      componentProps: {
        buttons,
      },
    });
    await modal.present();
  }
}
